/**
 * PHI Protection for EASI 2.0 Frontend
 *
 * Implements client-side PHI safeguards:
 * - Console logging disabled in production
 * - Sensitive data cleared on logout
 * - Session timeout monitoring
 * - Browser storage security
 *
 * HIPAA Requirements:
 * - 164.312(a)(2)(iii): Automatic logoff
 * - 164.312(a)(2)(iv): Encryption/decryption
 * - 164.530(c): Safeguards for PHI
 */

import { config } from '../config/environment';

/**
 * PHI field patterns that should never be logged
 */
const PHI_PATTERNS = {
  ssn: /\b\d{3}-\d{2}-\d{4}\b/g,
  phone: /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g,
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
  dateOfBirth: /\b(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/\d{4}\b/g,
  medicalRecordNumber: /\bMRN[:\s]?\d+\b/gi,
};

/**
 * Fields that contain PHI and should be handled carefully
 */
const PHI_FIELD_NAMES = [
  'firstName',
  'lastName',
  'dateOfBirth',
  'ssn',
  'socialSecurityNumber',
  'address',
  'phoneNumber',
  'email',
  'medicalRecordNumber',
  'patientId',
  'diagnosis',
  'transcript',
  'clinicalNotes',
];

/**
 * Storage keys that contain sensitive data
 */
const SENSITIVE_STORAGE_KEYS = [
  'accessToken',
  'idToken',
  'refreshToken',
  'easi_access_token',
  'easi_id_token',
  'easi_refresh_token',
  'easi_last_activity',
  'easi_remember_me',
  'easi_signature_block',
  'easi_signer_credentials',
  'user',
  'session',
  'patient',
  'transcript',
  'analysis',
  'report',
];

/**
 * Original console methods for restoration
 */
const originalConsole = {
  log: console.log,
  info: console.info,
  debug: console.debug,
  warn: console.warn,
  error: console.error,
};

/**
 * Check if a value potentially contains PHI
 */
export function containsPHI(value: unknown): boolean {
  if (!value) return false;

  const stringValue = typeof value === 'string' ? value : JSON.stringify(value);

  // Check against PHI patterns
  for (const pattern of Object.values(PHI_PATTERNS)) {
    if (pattern.test(stringValue)) {
      return true;
    }
  }

  // Check for PHI field names in objects
  if (typeof value === 'object' && value !== null) {
    const keys = Object.keys(value);
    for (const key of keys) {
      if (PHI_FIELD_NAMES.some((field) => key.toLowerCase().includes(field.toLowerCase()))) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Redact PHI from a value for safe logging
 */
export function redactPHI(value: unknown): unknown {
  if (!value) return value;

  if (typeof value === 'string') {
    let redacted = value;
    for (const [name, pattern] of Object.entries(PHI_PATTERNS)) {
      redacted = redacted.replace(pattern, `[REDACTED-${name.toUpperCase()}]`);
    }
    return redacted;
  }

  if (Array.isArray(value)) {
    return value.map(redactPHI);
  }

  if (typeof value === 'object' && value !== null) {
    const redacted: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      if (PHI_FIELD_NAMES.some((field) => key.toLowerCase().includes(field.toLowerCase()))) {
        redacted[key] = '[REDACTED]';
      } else {
        redacted[key] = redactPHI(val);
      }
    }
    return redacted;
  }

  return value;
}

/**
 * Initialize PHI protection for the application
 *
 * In production:
 * - Disables most console methods
 * - Only allows console.error with PHI redaction
 *
 * In development:
 * - Adds PHI warnings to console output
 */
export function initializePHIProtection(): void {
  if (config.isProduction) {
    // Disable console methods in production
    console.log = () => {};
    console.info = () => {};
    console.debug = () => {};
    console.warn = () => {};

    // Keep error logging but redact PHI
    console.error = (...args: unknown[]) => {
      const redactedArgs = args.map(redactPHI);
      originalConsole.error.apply(console, redactedArgs);
    };
  } else if (config.isDevelopment) {
    // In development, warn about PHI in console output
    const wrapWithPHICheck = (method: (...args: unknown[]) => void) => {
      return (...args: unknown[]) => {
        if (args.some(containsPHI)) {
          originalConsole.warn('[PHI WARNING] Console output may contain PHI');
        }
        method.apply(console, args);
      };
    };

    console.log = wrapWithPHICheck(originalConsole.log);
    console.info = wrapWithPHICheck(originalConsole.info);
    console.debug = wrapWithPHICheck(originalConsole.debug);
  }
}

/**
 * Restore original console methods (for testing)
 */
export function restoreConsole(): void {
  console.log = originalConsole.log;
  console.info = originalConsole.info;
  console.debug = originalConsole.debug;
  console.warn = originalConsole.warn;
  console.error = originalConsole.error;
}

/**
 * Clear all sensitive data from browser storage
 *
 * Call this on logout to ensure no PHI persists
 */
export function clearSensitiveData(): void {
  // Clear localStorage
  for (const key of SENSITIVE_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }

  // Clear all items that might contain PHI
  const localStorageKeys = Object.keys(localStorage);
  for (const key of localStorageKeys) {
    if (
      PHI_FIELD_NAMES.some((field) => key.toLowerCase().includes(field.toLowerCase())) ||
      key.startsWith('amplify') ||
      key.startsWith('CognitoIdentity')
    ) {
      localStorage.removeItem(key);
    }
  }

  // Clear sessionStorage
  for (const key of SENSITIVE_STORAGE_KEYS) {
    sessionStorage.removeItem(key);
  }

  const sessionStorageKeys = Object.keys(sessionStorage);
  for (const key of sessionStorageKeys) {
    if (
      PHI_FIELD_NAMES.some((field) => key.toLowerCase().includes(field.toLowerCase())) ||
      key.startsWith('amplify') ||
      key.startsWith('CognitoIdentity')
    ) {
      sessionStorage.removeItem(key);
    }
  }

  // Clear cookies (non-HttpOnly ones)
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name] = cookie.split('=');
    if (name) {
      document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    }
  }

  // Clear IndexedDB if used
  if (typeof indexedDB !== 'undefined') {
    // Clear Amplify datastores
    const dbNames = ['amplify-datastore', 'amplify-storage'];
    for (const dbName of dbNames) {
      try {
        indexedDB.deleteDatabase(dbName);
      } catch (e) {
        // Ignore errors from missing databases
      }
    }
  }
}

/**
 * Session activity tracking for automatic logoff
 */
class SessionActivityTracker {
  private lastActivity: number;
  private warningShown: boolean = false;
  private readonly timeoutMs: number;
  private readonly warningMs: number;
  private intervalId: number | null = null;
  private onTimeout?: () => void;
  private onWarning?: () => void;
  private boundRecordActivity: (() => void) | null = null;
  private static readonly ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];

  constructor(timeoutMs: number, warningMs: number) {
    this.timeoutMs = timeoutMs;
    this.warningMs = warningMs;
    this.lastActivity = Date.now();
  }

  /**
   * Start tracking session activity
   */
  start(onTimeout: () => void, onWarning?: () => void): void {
    this.onTimeout = onTimeout;
    this.onWarning = onWarning;
    this.lastActivity = Date.now();
    this.warningShown = false;

    // Listen for user activity (store bound reference for cleanup)
    this.boundRecordActivity = this.recordActivity.bind(this);

    for (const event of SessionActivityTracker.ACTIVITY_EVENTS) {
      document.addEventListener(event, this.boundRecordActivity, { passive: true });
    }

    // Check for timeout periodically
    this.intervalId = window.setInterval(() => {
      this.checkTimeout();
    }, 10000); // Check every 10 seconds
  }

  /**
   * Stop tracking — clears interval AND removes event listeners
   */
  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.boundRecordActivity) {
      for (const event of SessionActivityTracker.ACTIVITY_EVENTS) {
        document.removeEventListener(event, this.boundRecordActivity);
      }
      this.boundRecordActivity = null;
    }
  }

  /**
   * Record user activity (only resets if warning dialog is not yet showing)
   */
  recordActivity(): void {
    if (!this.warningShown) {
      this.lastActivity = Date.now();
    }
  }

  /**
   * Record API activity — always resets regardless of warning state.
   * Active API calls (polling, analysis, report generation) mean the system
   * is working on the clinician's behalf. They are NOT idle.
   */
  recordApiActivity(): void {
    this.lastActivity = Date.now();
    this.warningShown = false;
  }

  /**
   * Explicitly reset session — called when user clicks "Continue Session"
   */
  resetSession(): void {
    this.lastActivity = Date.now();
    this.warningShown = false;
  }

  /**
   * Check if session has timed out
   */
  private checkTimeout(): void {
    const elapsed = Date.now() - this.lastActivity;

    if (elapsed >= this.timeoutMs) {
      // Session timed out
      this.stop();
      clearSensitiveData();
      this.onTimeout?.();
    } else if (elapsed >= this.timeoutMs - this.warningMs && !this.warningShown) {
      // Show warning before timeout
      this.warningShown = true;
      this.onWarning?.();
    }
  }

  /**
   * Get remaining session time in milliseconds
   */
  getRemainingTime(): number {
    const elapsed = Date.now() - this.lastActivity;
    return Math.max(0, this.timeoutMs - elapsed);
  }
}

/**
 * Create session activity tracker with HIPAA-compliant settings
 */
export function createSessionTracker(timeoutOverrideMs?: number): SessionActivityTracker {
  return new SessionActivityTracker(
    timeoutOverrideMs ?? config.security.sessionTimeoutMs,
    config.security.sessionWarningMs
  );
}

/**
 * Validate HTTPS connection in production
 */
export function validateSecureConnection(): boolean {
  if (config.security.requireHttps && typeof window !== 'undefined') {
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      return false;
    }
  }
  return true;
}

/**
 * Simple encryption for sensitive localStorage data
 *
 * Note: This is not cryptographically secure but provides obfuscation
 * for data at rest in browser storage. For true security, rely on
 * server-side encryption and minimal client-side storage.
 */
const STORAGE_KEY = 'easi_storage_key';

function getStorageKey(): string {
  let key = sessionStorage.getItem(STORAGE_KEY);
  if (!key) {
    key = crypto.randomUUID();
    sessionStorage.setItem(STORAGE_KEY, key);
  }
  return key;
}

export function encryptForStorage(data: string): string {
  const key = getStorageKey();
  const encoded = new TextEncoder().encode(data);
  const keyBytes = new TextEncoder().encode(key.padEnd(32, '0').slice(0, 32));

  // Simple XOR obfuscation
  const encrypted = new Uint8Array(encoded.length);
  for (let i = 0; i < encoded.length; i++) {
    encrypted[i] = encoded[i] ^ keyBytes[i % keyBytes.length];
  }

  return btoa(String.fromCharCode(...encrypted));
}

export function decryptFromStorage(encrypted: string): string {
  try {
    const key = getStorageKey();
    const decoded = Uint8Array.from(atob(encrypted), (c) => c.charCodeAt(0));
    const keyBytes = new TextEncoder().encode(key.padEnd(32, '0').slice(0, 32));

    // Reverse XOR
    const decrypted = new Uint8Array(decoded.length);
    for (let i = 0; i < decoded.length; i++) {
      decrypted[i] = decoded[i] ^ keyBytes[i % keyBytes.length];
    }

    return new TextDecoder().decode(decrypted);
  } catch {
    return '';
  }
}

/**
 * Secure storage wrapper with encryption
 */
export const secureStorage = {
  setItem(key: string, value: string): void {
    const encrypted = encryptForStorage(value);
    localStorage.setItem(`secure_${key}`, encrypted);
  },

  getItem(key: string): string | null {
    const encrypted = localStorage.getItem(`secure_${key}`);
    if (!encrypted) return null;
    return decryptFromStorage(encrypted);
  },

  removeItem(key: string): void {
    localStorage.removeItem(`secure_${key}`);
  },
};

export default {
  initializePHIProtection,
  restoreConsole,
  clearSensitiveData,
  createSessionTracker,
  validateSecureConnection,
  containsPHI,
  redactPHI,
  secureStorage,
};
